import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { TableSessionsFetchAPI } from "../../../api/TableSessions";
import { PrinterFetchAPI } from "../../../api/Printer";
import { printData } from "../../../utils/PrintData";
import { kinposQrPrintBridge } from "../../../utils/KinposQrPrintBridge";

// "Generar QR de mesa" (POS "Mesas", pospago flow). Unlike PrepayQrModal.jsx, this QR is
// persistent: it maps to the table's public_access_code (see services/tableSessions.js#getTableQrData
// on the backend), so it survives across scans until someone regenerates it or disables access.
// The customer scans it to request access; a waiter still has to approve the session, same as the
// regular non-QR table flow.
export default function PospagoQrModal({ show, onClose, tableId, tableName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrData, setQrData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const androidQrPrintAvailable = kinposQrPrintBridge.isAvailable();

  const load = async () => {
    if (!tableId) return;
    setLoading(true);
    setError("");
    setPrintError("");
    setCopied(false);
    try {
      const token = localStorage.getItem("x-access-machine-token");
      const response = await TableSessionsFetchAPI.getTableQr(tableId, token);
      setQrData(response?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo obtener el QR. Intenta de nuevo.");
      setQrData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setConfirmingRegenerate(false);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tableId]);

  useEffect(() => {
    if (!show) return;
    if (androidQrPrintAvailable) {
      setPrinters([]);
      return;
    }
    (async () => {
      try {
        const machine = JSON.parse(localStorage.getItem("machine"));
        const token = localStorage.getItem("x-access-machine-token");
        const response = await PrinterFetchAPI.getPrinter(token, machine.id);
        setPrinters(response?.data?.printers || []);
      } catch (err) {
        setPrinters([]);
      }
    })();
  }, [show, androidQrPrintAvailable]);

  const toggleAccess = async () => {
    if (!tableId || !qrData || togglingAccess) return;
    setTogglingAccess(true);
    setError("");
    try {
      const token = localStorage.getItem("x-access-machine-token");
      const nextEnabled = !(Number(qrData.table_access_enabled) === 1);
      const response = await TableSessionsFetchAPI.setTableAccess(tableId, nextEnabled, token);
      setQrData(response?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo actualizar el acceso de la mesa.");
    } finally {
      setTogglingAccess(false);
    }
  };

  const regenerate = async () => {
    if (!tableId || regenerating) return;
    setRegenerating(true);
    setError("");
    setPrintError("");
    setCopied(false);
    try {
      const token = localStorage.getItem("x-access-machine-token");
      const response = await TableSessionsFetchAPI.regenerateTableCode(tableId, token);
      setQrData(response?.data?.data || null);
      setConfirmingRegenerate(false);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo regenerar el código de la mesa.");
    } finally {
      setRegenerating(false);
    }
  };

  const copyUrl = async () => {
    if (!qrData?.public_url) return;
    try {
      await navigator.clipboard.writeText(qrData.public_url);
    } catch (_) {
      const input = document.createElement("textarea");
      input.value = qrData.public_url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const printTicket = async () => {
    if (!qrData?.public_url || printing) return;
    setPrinting(true);
    setPrintError("");
    try {
      if (androidQrPrintAvailable) {
        // Deliberately omits `source` (defaults to "table_prepay" in buildTableQrPrintRequest) -
        // Android only ever accepted printing with that exact operation+source combo. A dedicated
        // "table_pospago" source (with or without its own operation name) was tried before and
        // Android rejected it every time, even though the request was otherwise valid - so this
        // mirrors the proven-working prepay request byte-for-byte except for the actual QR value
        // and the printed copy (subtitle/footer), which is all Android needs to print correctly.
        kinposQrPrintBridge.printTableQr({
          qrValue: qrData.public_url,
          tableId,
          tableName,
          subtitle: "Escanea y ordena",
          footer: "Codigo permanente de la mesa",
        });
        return;
      }

      if (printers.length === 0) {
        window.print();
        return;
      }

      await printData({
        Texto: `Mesa ${tableName || ""}\nEscanea y ordena\nCodigo permanente de la mesa`,
        QR: qrData.public_url,
      }, printers);
    } catch (err) {
      setPrintError(err?.message || "No se pudo enviar el QR a la impresora.");
    } finally {
      setPrinting(false);
    }
  };

  const printTicketInBrowser = () => {
    window.print();
  };

  const accessEnabled = Number(qrData?.table_access_enabled) === 1;

  return (

    <>
      {show && (
        <div className="ord-modal-backdrop show d-print-none" onMouseDown={onClose}>
          <div className="ord-modal ord-scope" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ width: "min(460px, 100%)" }}>
            <div className="ord-modal-header d-print-none">
              <div className="ord-modal-title">QR de mesa — {tableName || "Mesa"}</div>
              <button className="ord-btn ord-btn-secondary ord-btn-icon" type="button" onClick={onClose} title="Cerrar">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="ord-modal-body">
              {loading ? (
                <div className="ord-qr-state d-print-none">Cargando QR...</div>
              ) : error ? (
                <div className="ord-qr-state d-print-none">
                  <p className="ord-qr-error">{error}</p>
                  <button className="ord-btn ord-btn-primary" type="button" onClick={load}>
                    Reintentar
                  </button>
                </div>
              ) : qrData?.public_url ? (
                <div className="ord-qr">
                  <p className="ord-qr-hint d-print-none">
                    El cliente escanea este QR para pedir acceso a la mesa; un mesero debe aprobar la
                    solicitud. Este código es permanente: sigue funcionando hasta que lo regeneres o
                    desactives el acceso.
                  </p>

                  <div className="d-print-none" style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 12px" }}>
                    <div className="form-check form-switch" style={{ margin: 0 }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="pospago-qr-access-toggle"
                        checked={accessEnabled}
                        disabled={togglingAccess}
                        onChange={toggleAccess}
                      />
                      <label className="form-check-label" htmlFor="pospago-qr-access-toggle">
                        Acceso {accessEnabled ? "habilitado" : "deshabilitado"}
                      </label>
                    </div>
                  </div>

                  {/* Only this block survives onto the printed page - see the #pospago-qr-print-area
                rules in reservation.css. */}
                  <div id="pospago-qr-print-area" className={`rfm-prepay-print-ticket${accessEnabled ? "" : " rfm-prepay-print-ticket-disabled"}`}>
                    <p className="rfm-prepay-print-title">{tableName ? `Mesa ${tableName}` : "Mesa"}</p>
                    <p className="rfm-prepay-print-subtitle">Escanea y ordena</p>
                    <div className="ord-qr-frame">
                      {accessEnabled ? (
                        <QRCode value={qrData.public_url} size={220} bgColor="#ffffff" fgColor="#000000" level="M" />
                      ) : (
                        <p className="ord-qr-state d-print-none">El acceso de esta mesa está deshabilitado.</p>
                      )}
                    </div>
                    <p className="rfm-prepay-print-note">Codigo permanente de la mesa</p>
                  </div>

                  <div className="ord-qr-url d-print-none">
                    <input className="ord-qr-input" value={qrData.public_url} readOnly aria-label="URL del QR de la mesa" />
                    <button className="ord-btn ord-btn-secondary" type="button" onClick={copyUrl}>
                      {copied ? "Copiada" : "Copiar"}
                    </button>
                  </div>
                  {printError ? <p className="ord-qr-error d-print-none">{printError}</p> : null}

                  {confirmingRegenerate ? (
                    <div className="ord-qr-state d-print-none" style={{ marginTop: 12 }}>
                      <p className="ord-qr-error">
                        Esto invalida el QR impreso actual. ¿Regenerar el código de la mesa?
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="ord-btn ord-btn-primary" type="button" onClick={regenerate} disabled={regenerating}>
                          {regenerating ? "Regenerando..." : "Sí, regenerar"}
                        </button>
                        <button className="ord-btn ord-btn-secondary" type="button" onClick={() => setConfirmingRegenerate(false)} disabled={regenerating}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="ord-qr-state d-print-none">No fue posible obtener el QR.</div>
              )}
            </div>

            <div className="ord-modal-footer d-print-none">
              <div className="ord-footer">
                <button
                  className="ord-btn ord-btn-secondary"
                  type="button"
                  onClick={() => setConfirmingRegenerate(true)}
                  disabled={loading || !qrData?.public_url || confirmingRegenerate}
                >
                  <i className="bi bi-arrow-clockwise" /> Regenerar código
                </button>
                <button className="ord-btn ord-btn-secondary" type="button" onClick={printTicket} disabled={!qrData?.public_url || !accessEnabled || printing}>
                  <i className="bi bi-printer" /> {printing ? "Imprimiendo..." : "Imprimir"}
                </button>
                {/* {androidQrPrintAvailable || printers.length > 0 ? (
                  <button className="ord-btn ord-btn-link" type="button" onClick={printTicketInBrowser} disabled={!qrData?.public_url || !accessEnabled} title="Usar el dialogo de impresion del navegador en vez de la impresora configurada">
                    Imprimir con el navegador
                  </button>
                ) : null} */}
                <span className="ord-spacer" />
                <button className="ord-btn ord-btn-primary" type="button" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </>

  );
}
